import LegalPageLayout, { Sec, List } from "@/components/legal/LegalPageLayout";
import { LEGAL_DOCUMENTS } from "@/lib/legalDocuments";

export default function ApplicationAuthorization() {
  return (
    <LegalPageLayout
      document={LEGAL_DOCUMENTS.authorization}
      seoDescription="Funding Application Authorization and Verification Consent for Hannah Howard Funding."
    >
      <Sec n="1" title="Purpose">
        <p>This Funding Application Authorization and Verification Consent ("Authorization") authorizes Hannah Howard Funding ("Hannah Howard Funding," "Company," "we," "our," or "us") to review, evaluate, verify, and administer your application for a real estate commission advance.</p>
        <p>This Authorization supplements our Terms of Use, Privacy Policy, E-SIGN Consent, TCPA Consent, and any executed Funding Agreement.</p>
      </Sec>

      <Sec n="2" title="Applicant Authorization">
        <p>By submitting an application, uploading documents, electronically signing forms, or otherwise requesting a commission advance, you represent and authorize that:</p>
        <List items={[
          "all information you provide is true, accurate, complete, and current;",
          "you have authority to submit the information and documents provided;",
          "you understand that Hannah Howard Funding will rely upon this information when evaluating your application;",
          "you will promptly notify Hannah Howard Funding if any material information changes before funding or settlement.",
        ]}/>
      </Sec>

      <Sec n="3" title="Authorization to Verify Information">
        <p>You expressly authorize Hannah Howard Funding and its authorized service providers to verify information relating to your application.</p>
        <p>Verification may include, without limitation:</p>
        <List items={[
          "your identity;",
          "real estate license status;",
          "brokerage affiliation;",
          "commission entitlement;",
          "transaction status;",
          "expected closing date;",
          "anticipated commission amount;",
          "settlement information;",
          "property information;",
          "document authenticity;",
          "ownership or assignment of commission rights;",
          "any other information reasonably necessary to evaluate your application.",
        ]}/>
      </Sec>

      <Sec n="4" title="Authorization to Contact Third Parties">
        <p>You authorize Hannah Howard Funding to communicate with individuals and organizations involved in your transaction, including:</p>
        <List items={[
          "your managing broker;",
          "brokerage personnel;",
          "transaction coordinators;",
          "title companies;",
          "settlement or escrow companies;",
          "closing attorneys;",
          "lenders;",
          "cooperating brokers;",
          "other authorized transaction participants.",
        ]}/>
        <p>These communications may occur before funding, during underwriting, after funding, or as reasonably necessary to administer the transaction.</p>
      </Sec>

      <Sec n="5" title="Authorization to Exchange Information">
        <p>You authorize the exchange of information reasonably necessary to:</p>
        <List items={[
          "verify transaction details;",
          "confirm commission information;",
          "coordinate funding;",
          "facilitate repayment from settlement proceeds;",
          "investigate discrepancies;",
          "resolve transaction issues;",
          "prevent fraud;",
          "comply with legal obligations.",
        ]}/>
        <p>Only information reasonably necessary for these purposes will be requested or disclosed.</p>
      </Sec>

      <Sec n="6" title="Identity Verification and Fraud Prevention">
        <p>You understand that Hannah Howard Funding may implement identity verification and fraud prevention procedures before approving funding.</p>
        <p>These procedures may include:</p>
        <List items={[
          "verification of government-issued identification;",
          "comparison of application information;",
          "document authentication;",
          "transaction verification;",
          "review of inconsistencies;",
          "requests for supplemental documentation.",
        ]}/>
        <p>If requested information cannot be satisfactorily verified, Hannah Howard Funding may delay, suspend, or deny your application.</p>
      </Sec>

      <Sec n="7" title="No Obligation to Fund">
        <p>You acknowledge that:</p>
        <List items={[
          "submission of an application does not guarantee approval;",
          "verification does not constitute approval;",
          "preliminary discussions do not constitute a commitment to fund;",
          "Hannah Howard Funding retains sole discretion to approve or decline applications to the extent permitted by applicable law.",
        ]}/>
        <p>Funding occurs only after execution of the applicable Funding Agreement and satisfaction of all required conditions.</p>
      </Sec>

      <Sec n="8" title="Continuing Authorization">
        <p>This Authorization remains effective throughout:</p>
        <List items={[
          "application review;",
          "underwriting;",
          "funding;",
          "repayment administration;",
          "settlement coordination;",
          "dispute resolution relating to the funded transaction;",
          "compliance and recordkeeping obligations.",
        ]}/>
        <p>Where permitted by applicable law, this Authorization survives completion of the funded transaction to the extent reasonably necessary for legal compliance, audits, enforcement of agreements, fraud prevention, or record retention.</p>
      </Sec>

      <Sec n="9" title="Electronic Records">
        <p>You agree that this Authorization may be executed electronically.</p>
        <p>Electronic signatures, checked acknowledgment boxes, authenticated clicks, electronic acceptance, and similar methods shall have the same legal effect as handwritten signatures to the extent permitted by applicable law.</p>
        <p>Electronic copies of this Authorization shall be deemed originals.</p>
      </Sec>

      <Sec n="10" title="Revocation">
        <p>You may request to revoke this Authorization before funding by contacting Hannah Howard Funding.</p>
        <p>Revocation may prevent Hannah Howard Funding from continuing to evaluate or process your application and may result in closure of the application if required verification cannot be completed.</p>
        <p>Revocation does not invalidate actions lawfully taken before the request was received.</p>
      </Sec>

      <Sec n="11" title="Contact Information">
        <p>Questions regarding this Authorization may be directed to:</p>
        <p>
          Hannah Howard Funding<br />
          Email: hello@hannahhowardfunding.com<br />
          Phone: (870) 919-9520<br />
          Mailing Address: 495 Brickell Ave Miami, FL 33131
        </p>
      </Sec>
    </LegalPageLayout>
  );
}
